import { createBrowserRouter } from "react-router";

import { AdminPage } from "./pages/admin/AdminPage.js";
import { SetupPage } from "./pages/setup/SetupPage.js";
import { CareerPage } from "./pages/CareerPage.js";
import { HallPage } from "./pages/HallPage.js";
import { ArchivePage } from "./pages/ArchivePage.js";
import { DisplayPage } from "./pages/display/DisplayPage.js";
import { ForgotPage } from "./pages/ForgotPage.js";
import { HomePage } from "./pages/HomePage.js";
import { HowToPlayPage } from "./pages/HowToPlayPage.js";
import { LobbyPage } from "./pages/LobbyPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";
import { PartyPage } from "./pages/party/PartyPage.js";
import { RecapPage } from "./pages/recap/RecapPage.js";
import { RulesPage } from "./pages/RulesPage.js";
import { RegisterPage } from "./pages/RegisterPage.js";
import { ResetPage } from "./pages/ResetPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { SignInLinkPage } from "./pages/SignInLinkPage.js";
import { VerifyPage } from "./pages/VerifyPage.js";

/**
 * Every route in one place.
 *
 * Note that `/display/:code` is a **sibling** route with its own root layout,
 * not a variant of `/party/:code` behind a flag. It gets packaged as a
 * standalone TV app in phase 10 (Tizen, webOS), and that is only cheap if it
 * never grew a dependency on the player shell — see ADR 0005. A lint in
 * `eslint.config.ts` enforces the boundary.
 */
export const router = createBrowserRouter([
  { path: "/", element: <HomePage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  // The three the mail links point at, and the reason they are frontend routes
  // rather than server ones: a prefetching mail client must not be able to spend
  // a single-use token (`src/mail/templates.rs`). The paths are *fixed by those
  // templates* — `/verify?token=`, `/reset?token=`, `/signin?token=`,
  // `/reset?token=`, `/forgot` — so renaming one here breaks every link
  // already sitting in somebody's inbox.
  //
  // The token is a **path segment** — `/verify/<token>` — rather than a query
  // parameter.
  //
  // Not a security boundary, and it should not be mistaken for one: a path is
  // exactly as visible as a query string in the URL bar, in history and in an
  // access log. What protects these links is that they are 256 bits of CSPRNG,
  // single use, short lived, and spent by a POST rather than by being opened.
  //
  // Two things it does buy. `?token=` **announces what the string is** to
  // anybody reading the URL over a shoulder or in a log; a path segment is just
  // an opaque path. And `location.search` is what analytics scripts, error
  // reporters and session recorders capture by habit, so a token that is never
  // in it cannot be swept up by a tool added later without thinking about it.
  //
  // Each also has a token-less route, which is NOT a fallback for the old
  // `?token=` form — that is gone, and nothing was in an inbox when it went.
  // It is the "you followed a link with nothing in it" screen, which each page
  // renders as the same dead-link state it shows for a spent token.
  { path: "/verify/:token", element: <VerifyPage /> },
  { path: "/verify", element: <VerifyPage /> },
  // Passwordless. The most powerful of the emailed tokens and the shortest
  // lived: opening this *is* signing in.
  { path: "/signin/:token", element: <SignInLinkPage /> },
  { path: "/signin", element: <SignInLinkPage /> },
  { path: "/forgot", element: <ForgotPage /> },
  { path: "/reset/:token", element: <ResetPage /> },
  { path: "/reset", element: <ResetPage /> },
  // Needs a session — a career is one of the two things an account is for
  // (ADR 0007). `src/progression/` has computed this since phase 8.
  { path: "/me", element: <CareerPage /> },
  // Phase 16j-2. Signed-in only, and it ranks account holders only — guests
  // play without accounts, so a board cannot name somebody who never signed up.
  { path: "/hall", element: <HallPage /> },
  // Phase 16j-3. Signed-in only, and it has to be: an archive names everybody
  // who played, guests included, or it is a record with holes in it.
  { path: "/archive", element: <ArchivePage /> },
  // Name, picture, password. Needs a session; acts only on the caller's own
  // profile, resolved server-side from it.
  { path: "/settings", element: <SettingsPage /> },
  // Phase 23. The rules as this installation actually plays them — every number
  // read from the same row a game is built from, because phase 22 made them
  // editable and a page carrying a copy of the defaults would be wrong the
  // moment somebody edited one. Reachable from the menu and from the cog inside
  // a party: a rule you can only read while playing is a rule you cannot settle
  // an argument with afterwards.
  { path: "/rules", element: <RulesPage /> },
  // Phase 24. The walkthrough on its own route, so a profile that dismissed it
  // can run it again — which is what makes "dismissed once, gone for good" a
  // safe rule rather than a trap.
  { path: "/how-to-play", element: <HowToPlayPage /> },
  { path: "/lobby/:code", element: <LobbyPage /> },
  { path: "/party/:code", element: <PartyPage /> },
  { path: "/display/:code", element: <DisplayPage /> },
  // The same page with **no room in the URL** — a packaged television (phase
  // 11c), which has no URL bar to be handed a code through. It shows a pairing
  // code instead and the host types it into their phone. Reachable in a browser
  // too, which is the only way any of this can be tested before there is a
  // `.wgt` and a television in developer mode.
  { path: "/display", element: <DisplayPage /> },
  // No socket and no seat: a recap is opened days later by somebody who may not
  // have played. The game id is the capability — see `src/http/recap.rs`.
  { path: "/recap/:gameId", element: <RecapPage /> },
  // Phase 13. The server answers 404 to anybody who is not a curator, so this
  // route is reachable and simply has nothing in it for them — the check is on
  // the server, never here.
  // First-run setup — phase 16g. Public by necessity: it is reached before any
  // account exists. The server answers 404 once an admin does, so the page
  // renders "nothing to set up" rather than a form.
  { path: "/setup", element: <SetupPage /> },
  { path: "/admin", element: <AdminPage /> },
  { path: "*", element: <NotFoundPage /> },
]);
